/**
 * MultiProviderSettings — redesigned for clarity and polish
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Link,
  Divider,
  IconButton,
  InputAdornment,
  Collapse,
  Paper,
  Alert,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Check as CheckIcon,
  Visibility,
  VisibilityOff,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  CheckCircle,
  RadioButtonUnchecked,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  PROVIDERS,
  PROVIDER_INFO,
  setApiKey,
  getAvailableProviders,
  getUsageStats,
  hasDefaultKey,
  getPrimaryProvider,
  setPrimaryProvider,
  getDisabledProviders,
  setProviderDisabled,
  applyProviderPrefs,
  RECOMMENDED_FREE_PROVIDERS,
} from '../services/llmService';
import { auth, db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import AIQuickSetup from './AIQuickSetup';

// ── Provider metadata augmentation ───────────────────────────────────────────
// Colour dots that give each provider visual identity
const PROVIDER_COLOR = {
  [PROVIDERS.OPENROUTER]: '#6366f1',  // indigo
  [PROVIDERS.GEMINI]:     '#4285f4',  // Google blue
  [PROVIDERS.GROQ]:       '#f97316',  // orange
  [PROVIDERS.PERPLEXITY]: '#06b6d4',  // cyan
  [PROVIDERS.CLAUDE]:     '#d97757',  // Anthropic terracotta
  [PROVIDERS.SARVAM]:     '#ff6600',  // saffron-orange (Indian flag)
};

// Human-readable placeholder hints
const PLACEHOLDER = {
  [PROVIDERS.OPENROUTER]: 'sk-or-v1-…',
  [PROVIDERS.GEMINI]:     'AIza…',
  [PROVIDERS.GROQ]:       'gsk_…',
  [PROVIDERS.PERPLEXITY]: 'pplx-…',
  [PROVIDERS.CLAUDE]:     'sk-ant-…',
  [PROVIDERS.SARVAM]:     'Your Sarvam subscription key…',
};

// Badge colour for MUI Chip
const BADGE_COLOR = {
  [PROVIDERS.OPENROUTER]: 'primary',
  [PROVIDERS.GEMINI]:     'primary',
  [PROVIDERS.GROQ]:       'warning',
  [PROVIDERS.PERPLEXITY]: 'info',
  [PROVIDERS.CLAUDE]:     'secondary',
  [PROVIDERS.SARVAM]:     'warning',
};

const PROVIDER_ORDER = [
  PROVIDERS.GEMINI,
  PROVIDERS.GROQ,
  PROVIDERS.CLAUDE,
  PROVIDERS.SARVAM,
  PROVIDERS.PERPLEXITY,
  PROVIDERS.OPENROUTER,
];

// ── Single provider card ──────────────────────────────────────────────────────

function ProviderCard({ provider, initialKey, hasUserKey, isDefault, isSecurelyConfigured, onSave, onRemove, usageStat, isPrimary, isDisabled, onSetPrimary, onToggleDisabled }) {
  const info = PROVIDER_INFO[provider];
  const color = PROVIDER_COLOR[provider];

  // activeOwn  = user entered their own key for this provider
  // activeDefault = using the app's shared evaluation key (OpenRouter only, session-only)
  // active     = any form of active (for visual styling)
  const activeOwn     = !!hasUserKey;
  const activeDefault = isDefault && !activeOwn;
  const active        = activeOwn || activeDefault;

  // isSecurelyConfigured = key is in secure storage (from Firestore sync) but not in localStorage
  // In this mode, show a masked field that is disabled until user clicks "Change Key"
  const [editing, setEditing] = useState(!isSecurelyConfigured);

  // When the async secure-storage check completes and flips isSecurelyConfigured to true,
  // lock the card so the masked field is shown (don't leave it in editing mode).
  useEffect(() => {
    if (isSecurelyConfigured) setEditing(false);
  }, [isSecurelyConfigured]);

  const [keyValue, setKeyValue]   = useState(initialKey || '');
  const [show, setShow]           = useState(false);
  const [open, setOpen]           = useState(!active);
  const [savedFlash, setSavedFlash] = useState(false);

  // When secure key loads async, collapse the card (it was open because active was false at mount)
  useEffect(() => {
    if (isSecurelyConfigured) setOpen(false);
  }, [isSecurelyConfigured]);

  const handleSave = () => {
    if (!keyValue.trim()) return;
    onSave(provider, keyValue.trim());
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const handleRemove = () => {
    onRemove(provider);
    setKeyValue('');
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: active ? color + '60' : 'divider',
        overflow: 'hidden',
        transition: 'border-color 0.2s, opacity 0.2s',
        opacity: isDisabled ? 0.55 : 1,
      }}
    >
      {/* ── Header row ── */}
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.5, cursor: 'pointer',
          bgcolor: active ? color + '08' : 'transparent',
          '&:hover': { bgcolor: active ? color + '12' : 'action.hover' },
          transition: 'background 0.15s',
        }}
      >
        {/* Coloured indicator */}
        <Box
          sx={{
            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
            bgcolor: active ? color : 'text.disabled',
            boxShadow: active ? `0 0 0 3px ${color}30` : 'none',
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1" fontWeight={600}>{info.name}</Typography>
            <Chip label={info.badge} size="small" color={BADGE_COLOR[provider]} sx={{ height: 20, fontSize: '0.7rem' }} />
            {activeOwn && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: '0.85rem !important' }} />}
                label="Active"
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {activeDefault && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: '0.85rem !important' }} />}
                label="Active (Shared)"
                size="small"
                color="info"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {isPrimary && (
              <Chip
                icon={<Star sx={{ fontSize: '0.85rem !important' }} />}
                label="Primary"
                size="small"
                color="secondary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {isDisabled && (
              <Chip label="Disabled" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
          {!open && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {activeOwn
                ? `Your key configured · ${usageStat ? `${usageStat.requests} requests` : ''}`
                : activeDefault
                  ? 'Using shared evaluation key · Add your own for unlimited access'
                  : 'No key configured'}
            </Typography>
          )}
        </Box>

        <Tooltip title={isDisabled ? 'Enable this provider to set it as primary' : (isPrimary ? 'Primary provider — tried first' : 'Set as primary provider')}>
          <span>
            <IconButton
              size="small"
              disabled={isDisabled}
              onClick={(e) => { e.stopPropagation(); onSetPrimary(provider); }}
              sx={{ flexShrink: 0, color: isPrimary ? color : 'text.disabled' }}
            >
              {isPrimary ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isDisabled ? 'Enable provider' : 'Disable provider'}>
          <Switch
            size="small"
            checked={!isDisabled}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onToggleDisabled(provider, !e.target.checked)}
          />
        </Tooltip>

        <IconButton size="small" sx={{ flexShrink: 0, color: 'text.secondary' }}>
          {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
        </IconButton>
      </Box>

      {/* ── Expanded body ── */}
      <Collapse in={open}>
        <Box sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {info.description}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            <strong>Free tier:</strong> {info.freeLimit}
          </Typography>

          {/* Default key notice — shown instead of input when shared key is active */}
          {activeDefault ? (
            <Box sx={{ mb: 1.5, px: 1.5, py: 1, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 1.5 }}>
              <Typography variant="caption" color="info.dark" sx={{ display: 'block', lineHeight: 1.5 }}>
                A shared evaluation key is active for your session. Add your own key below to get unlimited access and keep it across sessions.
              </Typography>
            </Box>
          ) : null}

          {/* Key input */}
          {isSecurelyConfigured && !editing ? (
            /* Synced from cloud — show masked field + Change Key button */
            <TextField
              fullWidth
              size="small"
              disabled
              value="••••••••••••••••••••"
              variant="outlined"
              sx={{ mb: 1.5 }}
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.secondary' },
                endAdornment: (
                  <InputAdornment position="end">
                    <CheckCircle color="success" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              type={show ? 'text' : 'password'}
              placeholder={activeDefault ? 'Add your own key to replace the shared one…' : PLACEHOLDER[provider]}
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              variant="outlined"
              sx={{ mb: 1.5 }}
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow(s => !s)} edge="end" size="small">
                      {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {/* Action row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {isSecurelyConfigured && !editing ? (
              /* Synced key — offer Change and Remove */
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { setEditing(true); setKeyValue(''); }}
                  sx={{ borderRadius: 1.5 }}
                >
                  Change Key
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleRemove}
                  sx={{ borderRadius: 1.5 }}
                >
                  Remove
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  disabled={!keyValue.trim() || savedFlash}
                  startIcon={savedFlash ? <CheckIcon /> : null}
                  sx={{
                    borderRadius: 1.5, px: 2, minWidth: 80,
                    bgcolor: savedFlash ? 'success.main' : undefined,
                    '&:hover': { bgcolor: savedFlash ? 'success.dark' : undefined },
                  }}
                >
                  {savedFlash ? 'Saved!' : 'Save Key'}
                </Button>

                {activeOwn && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={handleRemove}
                    sx={{ borderRadius: 1.5 }}
                  >
                    Remove
                  </Button>
                )}
              </>
            )}

            <Link
              href={info.signupUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.3 }}
            >
              Get free key <OpenInNew sx={{ fontSize: 11 }} />
            </Link>
          </Box>

          {/* Usage */}
          {usageStat && activeOwn && (
            <Box sx={{ mt: 1.5, px: 1.5, py: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {usageStat.requests} requests · {(usageStat.totalDuration / 1000).toFixed(1)}s total response time
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function MultiProviderSettings({ isDemoAccount = false }) {
  const [keys, setKeys]                 = useState({});
  const [secureKeys, setSecureKeys]     = useState({}); // providers with keys in secure storage (from Firestore sync)
  const [usageStats, setUsageStats]     = useState({});
  const [usingSharedDefaults, setUsingSharedDefaults] = useState(false);
  const [syncStatus, setSyncStatus]     = useState(null); // null | 'saved' | 'error'
  const [primaryProvider, setPrimaryProviderState] = useState(null);
  const [disabledProviders, setDisabledProvidersState] = useState([]);
  const [prefsWarning, setPrefsWarning] = useState(null);

  const visibleProviders = isDemoAccount
    ? PROVIDER_ORDER
    : PROVIDER_ORDER.filter(p => p !== PROVIDERS.OPENROUTER);

  useEffect(() => {
    const KEY_MAP = {
      [PROVIDERS.OPENROUTER]: 'openrouter_api_key',
      [PROVIDERS.GEMINI]:     'gemini_pat',
      [PROVIDERS.GROQ]:       'groq_api_key',
      [PROVIDERS.PERPLEXITY]: 'perplexity_api_key',
      [PROVIDERS.CLAUDE]:     'claude_api_key',
      [PROVIDERS.SARVAM]:     'sarvam_api_key',
    };

    // Load user-configured keys from localStorage (legacy store)
    const loaded = {};
    Object.values(PROVIDERS).forEach(p => {
      const k = localStorage.getItem(KEY_MAP[p]);
      if (k) loaded[p] = k;
    });
    setKeys(loaded);
    setUsageStats(getUsageStats());

    const defaultActive = RECOMMENDED_FREE_PROVIDERS.some(p => hasDefaultKey(p))
      || hasDefaultKey(PROVIDERS.OPENROUTER);
    setUsingSharedDefaults(defaultActive);

    setPrimaryProviderState(getPrimaryProvider());
    setDisabledProvidersState(getDisabledProviders());

    // Also check secure storage (async) — catches keys synced from Firestore on login
    getAvailableProviders().then(providers => {
      const secure = {};
      providers.forEach(p => {
        // Skip providers that only have a shared default key (no user key)
        if (hasDefaultKey(p) && !loaded[p]) return;
        // If not already loaded from localStorage, this key is in secure storage
        if (!loaded[p]) secure[p] = true;
      });
      setSecureKeys(secure);
    }).catch(() => { /* ignore — secure storage unavailable */ });

    // Pull provider prefs (primary + disabled list) synced from another device
    if (auth?.currentUser && db) {
      getDoc(doc(db, 'users', auth.currentUser.uid)).then(snap => {
        const prefs = snap.exists() ? snap.data()?.providerPrefs : null;
        if (prefs) {
          applyProviderPrefs(prefs);
          setPrimaryProviderState(getPrimaryProvider());
          setDisabledProvidersState(getDisabledProviders());
        }
      }).catch(() => { /* ignore — fall back to local prefs */ });
    }
  }, []);

  const syncProviderPrefs = (prefs) => {
    if (auth?.currentUser && db) {
      setDoc(doc(db, 'users', auth.currentUser.uid), { providerPrefs: prefs }, { merge: true })
        .catch(e => console.error('Failed to sync provider prefs to Firestore:', e));
    }
  };

  const handleSetPrimary = (provider) => {
    const next = primaryProvider === provider ? null : provider; // clicking the active star clears it
    setPrimaryProvider(next);
    setPrimaryProviderState(next);
    syncProviderPrefs({ primary: next, disabled: disabledProviders });
  };

  const handleToggleDisabled = (provider, isDisabled) => {
    if (isDisabled) {
      const stillEnabled = visibleProviders.filter(
        p => p !== provider && !disabledProviders.includes(p)
      );
      if (stillEnabled.length === 0) {
        setPrefsWarning('At least one provider must stay enabled — AI features would stop working otherwise.');
        setTimeout(() => setPrefsWarning(null), 4000);
        return;
      }
    }
    setProviderDisabled(provider, isDisabled);
    const nextDisabled = isDisabled
      ? [...disabledProviders, provider]
      : disabledProviders.filter(p => p !== provider);
    const nextPrimary = isDisabled && primaryProvider === provider ? null : primaryProvider;
    setDisabledProvidersState(nextDisabled);
    setPrimaryProviderState(nextPrimary);
    syncProviderPrefs({ primary: nextPrimary, disabled: nextDisabled });
  };

  const handleSave = async (provider, key) => {
    await setApiKey(provider, key);
    setKeys(prev => ({ ...prev, [provider]: key }));
    // Sync to Firestore using setDoc+merge so it works even if the user doc
    // doesn't yet have a providerKeys field (updateDoc would fail in that case).
    if (auth?.currentUser && db) {
      try {
        await setDoc(
          doc(db, 'users', auth.currentUser.uid),
          { providerKeys: { [provider]: key } },
          { merge: true }
        );
        setSyncStatus('saved');
        setTimeout(() => setSyncStatus(null), 3000);
      } catch (e) {
        console.error('Failed to sync provider key to Firestore:', e);
        setSyncStatus('error');
      }
    }
  };

  const handleRemove = async (provider) => {
    await setApiKey(provider, '');
    setKeys(prev => ({ ...prev, [provider]: '' }));
    // Remove from Firestore
    if (auth?.currentUser && db) {
      try {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          [`providerKeys.${provider}`]: deleteField(),
        });
        setSyncStatus('saved');
        setTimeout(() => setSyncStatus(null), 3000);
      } catch (e) {
        console.error('Failed to remove provider key from Firestore:', e);
        setSyncStatus('error');
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Cloud sync feedback */}
      {syncStatus === 'saved' && (
        <Alert severity="success" sx={{ py: 0.5, fontSize: '0.8rem' }}>
          Key saved and synced — it will load automatically on all your devices.
        </Alert>
      )}
      {syncStatus === 'error' && (
        <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.8rem' }}
          onClose={() => setSyncStatus(null)}>
          Key saved on this device but could not sync to cloud. It won't appear on other devices until you re-enter it there, or try saving again.
        </Alert>
      )}

      {prefsWarning && (
        <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.8rem' }}>
          {prefsWarning}
        </Alert>
      )}

      {/* Status strip */}
      {(() => {
        // Count providers with keys — include both localStorage keys and secure-storage keys
        const userKeyCount = visibleProviders.filter(
          p => (keys[p] && keys[p].trim()) || secureKeys[p]
        ).length;
        const onFreeTrial = usingSharedDefaults && userKeyCount === 0 && !isDemoAccount;
        const anyActive = userKeyCount > 0 || usingSharedDefaults;
        return (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: 2, borderRadius: 2,
            bgcolor: onFreeTrial ? 'warning.50' : anyActive ? 'success.50' : 'warning.50',
            border: '1px solid',
            borderColor: onFreeTrial ? 'warning.200' : anyActive ? 'success.200' : 'warning.200',
          }}>
            {(anyActive && !onFreeTrial)
              ? <CheckCircle color="success" fontSize="small" />
              : <RadioButtonUnchecked color="warning" fontSize="small" />}
            <Typography variant="body2" fontWeight={600}>
              {userKeyCount > 0
                ? `${userKeyCount} of ${visibleProviders.length} providers configured`
                : onFreeTrial
                  ? 'Using shared free trial — limited tokens available'
                  : 'No providers configured — add at least one to enable AI features'}
            </Typography>
          </Box>
        );
      })()}

      {/* Free trial callout for regular users on shared key */}
      {usingSharedDefaults && !isDemoAccount && (
        <Alert severity="warning" sx={{ borderRadius: 2, py: 0.75 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.25 }}>
            You are on a limited shared trial
          </Typography>
          <Typography variant="caption">
            A shared AI pool is active for evaluation. For unlimited access, add your own free API key below —
            <strong> Gemini</strong> (1,500/day), <strong>Groq</strong> (generous free tier), or <strong>Claude</strong> (best reasoning).
            All are free to sign up with no credit card.
          </Typography>
        </Alert>
      )}

      {/* Quick setup wizard */}
      <AIQuickSetup
        onKeySaved={(provider) => {
          setKeys(prev => ({ ...prev, [provider]: 'saved' }));
          setSecureKeys(prev => ({ ...prev, [provider]: true }));
        }}
        onSyncToFirestore={async (provider, key) => {
          if (auth?.currentUser && db) {
            await setDoc(
              doc(db, 'users', auth.currentUser.uid),
              { providerKeys: { [provider]: key } },
              { merge: true }
            );
            setSyncStatus('saved');
            setTimeout(() => setSyncStatus(null), 3000);
          }
        }}
      />

      {/* Recommended setup notice */}
      <Alert severity="info" sx={{ borderRadius: 2, py: 0.75 }}>
        <Typography variant="caption">
          {isDemoAccount
            ? <><strong>Recommended:</strong> <strong>Gemini 2.5 Flash</strong> (1,500 free/day) + <strong>Groq</strong> (fastest) + <strong>Claude Sonnet 5</strong> (best reasoning). Add <strong>OpenRouter</strong> as an optional aggregator.</>
            : <><strong>Out-of-the-box:</strong> Add <strong>Gemini</strong> (most generous free tier), <strong>Groq</strong> (fastest), or <strong>Claude</strong> (best for math/science) — all free, no credit card, under 2 minutes each.</>
          }
        </Typography>
      </Alert>

      {/* Provider cards */}
      <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
        Tap <StarBorder sx={{ fontSize: 12, verticalAlign: 'text-bottom' }} /> to set a provider as primary (tried first), or use the switch to turn a provider off entirely.
      </Typography>
      {visibleProviders.map(provider => (
        <ProviderCard
          key={provider}
          provider={provider}
          initialKey={keys[provider] || ''}
          hasUserKey={!!(keys[provider] && keys[provider].trim()) || !!secureKeys[provider]}
          isDefault={hasDefaultKey(provider) && !(keys[provider] && keys[provider].trim()) && !secureKeys[provider]}
          isSecurelyConfigured={!!secureKeys[provider] && !(keys[provider] && keys[provider].trim())}
          onSave={handleSave}
          onRemove={handleRemove}
          usageStat={usageStats[provider]}
          isPrimary={primaryProvider === provider}
          isDisabled={disabledProviders.includes(provider)}
          onSetPrimary={handleSetPrimary}
          onToggleDisabled={handleToggleDisabled}
        />
      ))}
    </Box>
  );
}

export default MultiProviderSettings;
